
export interface ClientDomainPattern {
  clientName: string
  primaryDomain: string
  keywords: string[]
  isPrimary: (domain: string) => boolean
  isSecondary: (domain: string) => boolean
}

export const CLIENT_PATTERNS: ClientDomainPattern[] = [
  {
    clientName: 'Yotpo',
    primaryDomain: 'yotpo.com',
    keywords: ['yotpo'],
    isPrimary: (domain: string) => domain.toLowerCase() === 'yotpo.com',
    isSecondary: (domain: string) => {
      const lowerDomain = domain.toLowerCase()
      return lowerDomain !== 'yotpo.com' && lowerDomain.includes('yotpo')
    }
  },
  {
    clientName: 'Loop',
    primaryDomain: 'loopreturns.com',
    keywords: ['loop'],
    isPrimary: (domain: string) => domain.toLowerCase() === 'loopreturns.com',
    isSecondary: (domain: string) => {
      const lowerDomain = domain.toLowerCase()
      return lowerDomain !== 'loopreturns.com' && lowerDomain.includes('loop')
    }
  }
]

export interface DomainCategorization {
  clientName: string | null
  domainType: 'primary' | 'secondary' | 'other'
  pattern?: ClientDomainPattern
}

export function categorizeDomain(domain: string): DomainCategorization {
  if (!domain) {
    return { clientName: null, domainType: 'other' }
  }

  for (const pattern of CLIENT_PATTERNS) {
    if (pattern.isPrimary(domain)) {
      return {
        clientName: pattern.clientName,
        domainType: 'primary',
        pattern
      }
    }
    
    if (pattern.isSecondary(domain)) {
      return {
        clientName: pattern.clientName,
        domainType: 'secondary',
        pattern
      }
    }
  }

  return { clientName: null, domainType: 'other' }
}

interface ClientStats {
  primary: number
  secondary: number
  total: number
}

interface OtherStats {
  total: number
}

export function getClientStats(mailboxes: any[]) {
  const stats: {
    Yotpo: ClientStats
    Loop: ClientStats
    Other: OtherStats
  } = {
    Yotpo: { primary: 0, secondary: 0, total: 0 },
    Loop: { primary: 0, secondary: 0, total: 0 },
    Other: { total: 0 }
  }

  mailboxes.forEach(mailbox => {
    const categorization = categorizeDomain(mailbox.domain)
    
    if (categorization.clientName) {
      const clientName = categorization.clientName as 'Yotpo' | 'Loop'
      const clientStats = stats[clientName]
      
      clientStats.total++
      if (categorization.domainType === 'primary') {
        clientStats.primary++
      } else if (categorization.domainType === 'secondary') {
        clientStats.secondary++
      }
    } else {
      stats.Other.total++
    }
  })

  return stats
}

export function autoAssignClientName(domain: string): string | null {
  const categorization = categorizeDomain(domain)
  return categorization.clientName
}

// New function to determine if a mailbox is healthy based on reputation
export function isHealthyMailbox(mailbox: any): boolean {
  // Check if reputation_score exists and is 100, or if it's null/undefined (N/A)
  const hasReputationScore = mailbox.reputation_score !== null && mailbox.reputation_score !== undefined
  
  if (hasReputationScore) {
    // If reputation score exists, it must be 100% to be considered healthy
    return mailbox.reputation_score === 100
  } else {
    // If reputation score is N/A, consider other health indicators
    const healthScore = mailbox.health_score || 0
    const bounceRate = mailbox.bounce_rate || 0
    const smtpSuccess = mailbox.smtp_success
    const imapSuccess = mailbox.imap_success
    
    return healthScore >= 80 && bounceRate <= 5 && smtpSuccess && imapSuccess
  }
}

// New function to filter mailboxes by domain type for a specific client
export function filterMailboxesByDomainType(
  mailboxes: any[], 
  clientName: string, 
  domainType: 'primary' | 'secondary' | 'all'
): any[] {
  if (domainType === 'all') {
    return mailboxes.filter(m => m.client_name === clientName)
  }
  
  return mailboxes.filter(mailbox => {
    if (mailbox.client_name !== clientName) return false
    
    const categorization = categorizeDomain(mailbox.domain)
    return categorization.domainType === domainType
  })
}
